import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API from "../utils/API";

// Section for Utilities and Context
import "./legislation.css";
import { AuthContext } from '../utils/AuthContext';
import { sortBillsByMode, computeCalendarHeaders } from '../utils/billSortGrouping';
import { deriveBillDisplayFields } from '../utils/billDisplay';
import CreateReportModal from '../components/CreateReportModal';
import PageTitle from '../components/PageTitle';
import { getCurrentSession } from '../utils/session';

const TAG_COLORS = {
  purple: { bg: '#e8e4f4', text: '#3d2e7a' },
  teal:   { bg: '#cfe8ef', text: '#1a4a5a' },
  sage:   { bg: '#d5eddf', text: '#1a4a2e' },
  amber:  { bg: '#fdebd0', text: '#6b3d0a' },
  rose:   { bg: '#fcd9e2', text: '#6b1a2e' },
  slate:  { bg: '#dde3ec', text: '#2b3a4c' },
  indigo: { bg: '#dce0f5', text: '#2d2a7e' },
  coral:  { bg: '#fce0d8', text: '#6b2a1a' },
};
const DEFAULT_TEAM_TAG = { bg: '#e8e4f4', text: '#3d2e7a' };

const formatSessionType = (num) => {
  if (num >= 1) return `${num}th Extraordinary`;
  else return 'Regular';
};

const FILTER_STORAGE_KEY = 'legislation_filters';
const FILTER_TTL_MS = 60 * 60 * 1000; // 1 hour
const CART_BAR_WIDTH_RATIO = 0.8; // report cart bar is narrower than the legislation container, centered within it

// Calendar-date string (YYYY-MM-DD) for "today + offsetDays" in Pacific time.
function ptDateString(offsetDays = 0) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
  const [y, m, d] = parts.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + offsetDays);
  return dt.toISOString().slice(0, 10);
}

// Global, built-in saved filters available to every user. These aren't stored in
// saved_filters and have no rename/delete UI, so they can't be edited or removed.
const BUILTIN_FILTERS = [
  { id: 'builtin-daily-introduced', name: 'Daily Introduced', key: 'version_introduced' },
  { id: 'builtin-daily-amended', name: 'Daily Amended', key: 'version_amended' },
];


// Recognized bill-type prefixes — used to tell a bill lookup (e.g. "SB 123") apart
// from a subject/keyword search that happens to be a short word (e.g. "gig", "tax").
const BILL_TYPES = ['AB', 'ACA', 'ACR', 'AJR', 'HR', 'SB', 'SR', 'SCR', 'SCA', 'SJR', 'GRP'];

const DEFAULT_FILTERS = {
  search: '',
  author: '',
  location: '',
  session: getCurrentSession(),
  session_number: '0',
  status: '',
  vote_required: '',
  fiscal_committee: '',
  local_program: '',
  urgency: '',
  tax_levy: '',
  version_date_from: '',
  version_date_to: '',
  version_introduced: '',
  version_amended: '',
  showDigest: false,
  showAi: false,
  trackedBy: '',
  trackedByClient: '',
  tag_ids: '',
  sort: 'newest'
};

const DEFAULT_APPLIED_FILTERS = { session: getCurrentSession(), session_number: '0', sort: 'newest' };

function loadSavedFilters() {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.savedAt > FILTER_TTL_MS) {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Single-select dropdown styled to match the Tags filter's custom panel (background/hover),
// since native <select> option lists are OS-rendered and can't be restyled with CSS.
function FilterDropdown({ name, value, options, onChange, disabled, autoWidth, multi, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find(opt => String(opt.value) === String(value));
  const selectedValues = multi ? (value ? value.split(',') : []) : [];
  const toggleLabel = multi
    ? (selectedValues.length > 0 ? `${selectedValues.length} selected` : 'Any')
    : (selected ? selected.label : 'Any');

  return (
    <div className={`legislation-dropdown${autoWidth ? ' legislation-dropdown-autosize' : ''}`} ref={ref}>
      <button
        type="button"
        className="form-select legislation-filter-select py-1 legislation-dropdown-toggle"
        onClick={() => !disabled && setOpen(v => !v)}
        disabled={disabled}
      >
        {toggleLabel}
      </button>
      {autoWidth && (
        <div className="form-select legislation-filter-select py-1 legislation-dropdown-ghost" aria-hidden="true">
          {options.map(opt => (
            <span key={opt.value} className="legislation-dropdown-ghost-item">{opt.label}</span>
          ))}
        </div>
      )}
      {open && (
        <div className="legislation-dropdown-panel">
          {multi ? options.map(opt => {
            const checked = selectedValues.includes(String(opt.value));
            return (
              <label key={opt.value} className="legislation-dropdown-item form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(opt.value)}
                />
                <span className="legislation-dropdown-label form-check-label">{opt.label}</span>
              </label>
            );
          }) : options.map(opt => (
            <div
              key={opt.value}
              className={`legislation-dropdown-item${opt.disabled ? ' legislation-dropdown-item-disabled' : ''}`}
              onClick={() => {
                if (opt.disabled) return;
                onChange({ target: { name, value: String(opt.value) } });
                setOpen(false);
              }}
            >
              <span className="legislation-dropdown-label">{opt.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Legislation() {
  const { auth } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [trackedBills, setTrackedBills] = useState(new Set());
  const [teamTrackedBills, setTeamTrackedBills] = useState(new Map());
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [userTeams, setUserTeams] = useState([]);
  const [availableTeamTags, setAvailableTeamTags] = useState([]);
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);
  const tagsDropdownRef = useRef(null);
  const [currentBillForModal, setCurrentBillForModal] = useState(null);
  const [teamTrackingOptions, setTeamTrackingOptions] = useState([]); // { team_id, team_name, isTracking, bill_manage_permission, clientsLabel, clients: [{ client_id, client_name, isTracking }] }
  const [initialPersonalTracked, setInitialPersonalTracked] = useState(false);
  const [aiSummarySessions, setAiSummarySessions] = useState([]);
  const [individualSummaryState, setIndividualSummaryState] = useState(new Map()); // bill_id → { digest: bool, ai: bool }
  const [reviewedBills, setReviewedBills] = useState(new Map()); // bill_version_id → reviewed_at

  // Report building (Legislation Reports feature)
  const [reportMode, setReportMode] = useState(false);
  const [reportCart, setReportCart] = useState(new Map()); // bill_id → { bill, note }
  const [reportNoteBillId, setReportNoteBillId] = useState(null);
  const [showCreateReportModal, setShowCreateReportModal] = useState(false);
  const pageContainerRef = useRef(null);
  const [cartBarRect, setCartBarRect] = useState(null);

  // Saved filter presets
  const [savedFilterPresets, setSavedFilterPresets] = useState([]);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [editingPresetName, setEditingPresetName] = useState('');
  const [showQuickFilters, setShowQuickFilters] = useState(false);
  const [showSavedFilters, setShowSavedFilters] = useState(false);
  const [showMiscFilters, setShowMiscFilters] = useState(false);

  // Workspace (team-shared) saved filter presets
  const [teamSavedFilterPresets, setTeamSavedFilterPresets] = useState(new Map()); // team_id -> [presets]
  const [selectedFilterTeamId, setSelectedFilterTeamId] = useState(null);
  const [showTeamSaveForm, setShowTeamSaveForm] = useState(false);
  const [saveTeamFilterName, setSaveTeamFilterName] = useState('');
  const [editingTeamPresetId, setEditingTeamPresetId] = useState(null);
  const [editingTeamPresetName, setEditingTeamPresetName] = useState('');
  const [showTeamSavedFilters, setShowTeamSavedFilters] = useState(false);

  // Search filters state — restored from sessionStorage if < 1 hour old
  const [filters, setFilters] = useState(() => loadSavedFilters()?.filters ?? DEFAULT_FILTERS);

  // Applied filters state (to trigger search only on button click)
  const [appliedFilters, setAppliedFilters] = useState(() => loadSavedFilters()?.appliedFilters ?? DEFAULT_APPLIED_FILTERS);

  const [filtersAreDirty, setFiltersAreDirty] = useState(false);

  // Persist both filter states to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ filters, appliedFilters, savedAt: Date.now() }));
  }, [filters, appliedFilters]);

  const isCalendarView = appliedFilters.sort === 'calendar';
  const limit = isCalendarView ? 5000
    : (appliedFilters.location === 'Assembly Floor' || appliedFilters.location === 'Senate Floor') ? 200
    : 100;

  const observer = useRef();

  // Fetch bills with server-side pagination and filtering
  useEffect(() => {
    setLoading(true);

    // Ensure client-only sort keys (like 'file_location') are not sent to the backend.
    // We use 'newest' as a stable default for the server-side fetch.
    const apiFilters = { ...appliedFilters };
    if (appliedFilters.sort === 'calendar') apiFilters.calendar_only = 'true';
    if (['file_location', 'hearing_date', 'calendar'].includes(apiFilters.sort)) apiFilters.sort = 'newest';

    const params = {
      offset: (page - 1) * limit,
      limit,
      ...apiFilters
    };

    axios.get('/api/legislation/bills', { params })
    .then(response => {
      const newBills = response.data.rows;
      // If page is 1, replace bills. Otherwise, append.
      setBills(prev => page === 1 ? newBills : [...prev, ...newBills]);
      setHasMore(response.data.count > (page * limit));
      setLoading(false);
    }).catch(error => {
      console.error("Error fetching bills:", error);
      setLoading(false);
    });
  }, [page, appliedFilters]);

  // Fetch user's tracked bills, team memberships, clients, and saved filter presets
  useEffect(() => {
    if (auth.isAuthenticated) {
      API.getTrackedBills(auth.user.user_id)
        .then(res => setTrackedBills(new Set(res.data)))
        .catch(err => console.error("Error fetching tracked bills:", err));
      API.getReviewedBills(auth.user.user_id)
        .then(res => {
          const map = new Map();
          res.data.forEach(({ bill_version_id, reviewed_at }) => map.set(bill_version_id, reviewed_at));
          setReviewedBills(map);
        })
        .catch(err => console.error("Error fetching reviewed bills:", err));
      API.getTeamTrackedBills(auth.user.user_id)
        .then(res => {
          const map = new Map();
          res.data.forEach(({ bill_id, team_name, team_acronym, tag_color }) => {
            if (!map.has(bill_id)) map.set(bill_id, []);
            const label = team_acronym || team_name.split(/\s+/).map(w => w[0]).join('').toUpperCase();
            map.get(bill_id).push({ label, color: tag_color || null });
          });
          setTeamTrackedBills(map);
        })
        .catch(err => console.error("Error fetching team tracked bills:", err));
      API.getUserTeams({ user_id: auth.user.user_id })
        .then(res => setUserTeams(res.data))
        .catch(err => console.error("Error fetching user teams:", err));
      API.getSavedFilters()
        .then(res => setSavedFilterPresets(res.data))
        .catch(() => setSavedFilterPresets([]));
    } else {
      setTrackedBills(new Set());
      setTeamTrackedBills(new Map());
      setUserTeams([]);
      setSavedFilterPresets([]);
    }
  }, [auth.isAuthenticated]);

  // Fetch Workspace-shared saved filter presets for every team the user belongs to
  useEffect(() => {
    if (userTeams.length === 0) {
      setTeamSavedFilterPresets(new Map());
      setSelectedFilterTeamId(null);
      return;
    }
    setSelectedFilterTeamId(prev => (prev && userTeams.some(m => m.team_id === prev)) ? prev : userTeams[0].team_id);
    Promise.all(userTeams.map(m =>
      API.getTeamSavedFilters(m.team_id).then(res => [m.team_id, res.data]).catch(() => [m.team_id, []])
    )).then(entries => setTeamSavedFilterPresets(new Map(entries)));
  }, [userTeams]);

  // Fetch subject tags for the Tags filter, consolidated across every Workspace the user belongs to
  useEffect(() => {
    if (userTeams.length === 0 || !auth.user) { setAvailableTeamTags([]); return; }
    Promise.all(userTeams.map(m => {
      const teamLabel = m.TeamAccount?.team_acronym || m.TeamAccount?.team_name.split(/\s+/).map(w => w[0]).join('').toUpperCase();
      return API.getTeamSubjectTags(m.team_id, auth.user.user_id)
        .then(res => res.data.map(tag => ({ ...tag, team_label: teamLabel })))
        .catch(() => []);
    })).then(results => setAvailableTeamTags(results.flat()));
  }, [userTeams, auth.user]);

  // Close the Tags dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(e.target))
        setShowTagsDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // const displayedBills = filteredBills.slice(0, page * limit);
  // const hasMore = displayedBills.length < filteredBills.length;

  // Fetch sessions, statuses, and AI summary session list on mount
  useEffect(() => {
    axios.get('/api/legislation/sessions').then(response => {
      setSessions(response.data);
    }).catch(error => console.error("Error fetching sessions:", error));

    axios.get('/api/legislation/statuses').then(response => {
      setStatuses(response.data);
    }).catch(error => console.error("Error fetching statuses:", error));

    API.getAiSummarySessions().then(res => {
      setAiSummarySessions(res.data);
    }).catch(() => {});

  }, []);

  // Fetch session types and locations when session filter changes
  useEffect(() => {
    if (filters.session) {
      axios.get(`/api/legislation/session-types?session=${filters.session}`).then(response => {
        setSessionTypes(response.data);
      }).catch(error => console.error("Error fetching session types:", error));

      const asmFloor = { description: 'Asm Floor', location_code: 'Assembly Floor' };
      const senFloor = { description: 'Sen Floor', location_code: 'Senate Floor' };
      axios.get(`/api/legislation/locations?session=${filters.session}`).then(response => {
        setLocations([asmFloor, senFloor, ...response.data]);
      }).catch(error => console.error("Error fetching locations:", error));
    } else {
      setSessionTypes([]);
      setLocations([]);
    }
    // Reset session_number, author, location, and sort when session changes
    const defaultSort = filters.session === getCurrentSession() ? 'newest' : 'az';
    setFilters(prev => ({ ...prev, session_number: '0', author: '', location: '', sort: defaultSort }));
  }, [filters.session]);

  // Fetch legislators for Author dropdown — scoped to the selected session.
  // Strip the hyphen so the value matches the session_year format in the DB (e.g. '20252026').
  useEffect(() => {
    if (!filters.session) return;
    const sessionCode = filters.session.replace(/-/g, '');
    axios.get(`/api/legislation/legislators?session=${sessionCode}`)
      .then(res => setAuthors(res.data))
      .catch(err => console.error("Error fetching legislators:", err));
  }, [filters.session]);

  const filtersMatchDefault = Object.keys(DEFAULT_FILTERS).every(key => filters[key] === DEFAULT_FILTERS[key]);

  const buildAppliedFilters = useCallback((f) => {
    const result = {};
    result.session = f.session || getCurrentSession();

    if (f.search) {
      const input = f.search.trim();
      const billRegex = /^([a-zA-Z]{1,3})\s*(x(\d+))?\s*(\d+)?$/i;
      const numberRegex = /^\d+$/;
      const match = input.match(billRegex);
      if (numberRegex.test(input)) {
        result.bill_number = input;
      } else if (match && BILL_TYPES.includes(match[1].toUpperCase())) {
        result.bill_type = match[1].toUpperCase();
        if (match[3]) result.session_number = match[3];
        if (match[4]) result.bill_number = match[4];
      } else {
        result.query = input;
      }
    }

    if (!result.session_number) result.session_number = f.session_number || '0';
    if (f.author) result.legislators_tbl_id = f.author;
    if (f.location) result.location = f.location;

    const optionalFilters = ['status', 'vote_required', 'fiscal_committee', 'local_program', 'urgency', 'tax_levy', 'sort',
                             'version_date_from', 'version_date_to', 'version_introduced', 'version_amended'];
    optionalFilters.forEach(key => { if (f[key]) result[key] = f[key]; });

    if (f.trackedBy === 'myself') {
      result.trackedOnly = 'true';
      result.user_id = auth.user.user_id;
    } else if (f.trackedBy) {
      result.tracked_team_id = f.trackedBy;
    }

    if (f.trackedByClient) result.tracked_client_id = f.trackedByClient;
    if (f.tag_ids) result.tag_ids = f.tag_ids;

    return result;
  }, [auth.user]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setShowFilters(false);
    setFiltersAreDirty(false);
    setAppliedFilters(buildAppliedFilters(filters));
  };

  const handleReset = () => {
    sessionStorage.removeItem(FILTER_STORAGE_KEY);
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_APPLIED_FILTERS);
    setFiltersAreDirty(false);
    setPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersAreDirty(true);
    setFilters(prev => {
      const newFilters = { ...prev, [name]: value };
      if (name === 'location') {
        if (value !== 'Assembly Floor' && value !== 'Senate Floor' && prev.sort === 'file_location') {
          newFilters.sort = 'newest';
        }
      }
      if (name === 'trackedBy') newFilters.trackedByClient = '';
      if (name === 'session_number') newFilters.location = '';
      return newFilters;
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFiltersAreDirty(true);
    setFilters(prev => ({
      ...prev,
      [name]: checked ? value : ''
    }));
  };

  // Toggles a value in/out of a comma-joined multi-select filter (e.g. tag_ids, status)
  const handleMultiFilterToggle = (filterKey, itemValue) => {
    const valStr = String(itemValue);
    setFiltersAreDirty(true);
    setFilters(prev => {
      const current = prev[filterKey] ? prev[filterKey].split(',') : [];
      const next = current.includes(valStr) ? current.filter(v => v !== valStr) : [...current, valStr];
      return { ...prev, [filterKey]: next.join(',') };
    });
  };

  const handleSaveFilterPreset = async () => {
    if (!saveFilterName.trim()) return;
    try {
      const res = await API.createSavedFilter(saveFilterName.trim(), filters);
      setSavedFilterPresets(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setSaveFilterName('');
      setShowSaveForm(false);
    } catch (err) {
      console.error('Error saving filter:', err);
    }
  };

  const handleLoadFilterPreset = (preset) => {
    try {
      const loaded = typeof preset.filter_json === 'string'
        ? JSON.parse(preset.filter_json)
        : preset.filter_json;
      // Legacy saved filters (pre bill/subject merge) stored these as two separate
      // fields — fold them into the merged search box so the criteria still shows up
      // and re-applies. If both were set (rare), the bill lookup wins and the subject
      // constraint is dropped rather than trying to guess how to recombine them.
      if (!loaded.search && (loaded.bill_id_search || loaded.subject_search)) {
        loaded.search = loaded.bill_id_search || loaded.subject_search;
      }
      delete loaded.bill_id_search;
      delete loaded.subject_search;
      const loadedFilters = { ...DEFAULT_FILTERS, ...loaded };
      setFilters(loadedFilters);
      setPage(1);
      setShowFilters(false);
      setAppliedFilters(buildAppliedFilters(loadedFilters));
    } catch {
      console.error('Failed to parse saved filter');
    }
  };

  // Apply a filter preset handed off via navigation state (e.g. from the Workspace
  // page's Saved Filters tab), then clear it so back/forward doesn't reapply it.
  useEffect(() => {
    if (location.state?.loadFilter) {
      handleLoadFilterPreset(location.state.loadFilter);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleLoadBuiltinFilter = (builtin) => {
    const loadedFilters = {
      ...DEFAULT_FILTERS,
      [builtin.key]: 'true',
      version_date_from: ptDateString(-1),
      version_date_to: '',
    };
    setFilters(loadedFilters);
    setPage(1);
    setShowFilters(false);
    setAppliedFilters(buildAppliedFilters(loadedFilters));
  };

  const handleDeleteFilterPreset = async (filterId) => {
    try {
      await API.deleteSavedFilter(filterId);
      setSavedFilterPresets(prev => prev.filter(p => p.filter_id !== filterId));
      if (editingPresetId === filterId) setEditingPresetId(null);
    } catch (err) {
      console.error('Error deleting filter:', err);
    }
  };

  const handleRenameFilterPreset = async (filterId) => {
    if (!editingPresetName.trim()) return;
    try {
      await API.updateSavedFilter(filterId, editingPresetName.trim());
      setSavedFilterPresets(prev =>
        prev.map(p => p.filter_id === filterId ? { ...p, name: editingPresetName.trim() } : p)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingPresetId(null);
      setEditingPresetName('');
    } catch (err) {
      console.error('Error renaming filter:', err);
    }
  };

  // Workspace (team-shared) filter membership for the currently selected filter-team
  const selectedFilterTeamMembership = userTeams.find(m => m.team_id === selectedFilterTeamId);
  const isSelectedFilterTeamOwner = selectedFilterTeamMembership?.TeamAccount?.team_admin === auth.user?.user_id;
  const teamSavedFilterList = teamSavedFilterPresets.get(selectedFilterTeamId) || [];

  const handleSaveTeamFilterPreset = async () => {
    if (!saveTeamFilterName.trim() || !selectedFilterTeamId) return;
    try {
      const res = await API.createTeamSavedFilter(selectedFilterTeamId, auth.user.user_id, saveTeamFilterName.trim(), filters);
      setTeamSavedFilterPresets(prev => {
        const next = new Map(prev);
        next.set(selectedFilterTeamId, [...(next.get(selectedFilterTeamId) || []), res.data].sort((a, b) => a.name.localeCompare(b.name)));
        return next;
      });
      setSaveTeamFilterName('');
      setShowTeamSaveForm(false);
    } catch (err) {
      console.error('Error saving Workspace filter:', err);
    }
  };

  const handleDeleteTeamFilterPreset = async (filterId) => {
    if (!selectedFilterTeamId) return;
    try {
      await API.deleteTeamSavedFilter(selectedFilterTeamId, filterId, auth.user.user_id);
      setTeamSavedFilterPresets(prev => {
        const next = new Map(prev);
        next.set(selectedFilterTeamId, (next.get(selectedFilterTeamId) || []).filter(p => p.filter_id !== filterId));
        return next;
      });
      if (editingTeamPresetId === filterId) setEditingTeamPresetId(null);
    } catch (err) {
      console.error('Error deleting Workspace filter:', err);
    }
  };

  const handleRenameTeamFilterPreset = async (filterId) => {
    if (!editingTeamPresetName.trim() || !selectedFilterTeamId) return;
    try {
      await API.updateTeamSavedFilter(selectedFilterTeamId, filterId, auth.user.user_id, editingTeamPresetName.trim());
      setTeamSavedFilterPresets(prev => {
        const next = new Map(prev);
        next.set(selectedFilterTeamId,
          (next.get(selectedFilterTeamId) || [])
            .map(p => p.filter_id === filterId ? { ...p, name: editingTeamPresetName.trim() } : p)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        return next;
      });
      setEditingTeamPresetId(null);
      setEditingTeamPresetName('');
    } catch (err) {
      console.error('Error renaming Workspace filter:', err);
    }
  };

  const handleTogglePersonalTrack = async (bill) => {
    
    const isCurrentlyTracked = trackedBills.has(bill.bill_id);
    const billData = {
      user_id: auth.user.user_id,
      bill_id: bill.bill_id,
      bill_version_id: bill.bill_version_id,
      active_track: !isCurrentlyTracked
    };

    try {
      await API.trackBill(billData);
      setTrackedBills(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyTracked) {
          newSet.delete(bill.bill_id);
        } else {
          newSet.add(bill.bill_id);
        }
        return newSet;
      });
    } catch (error) {
      console.error("Error updating bill tracking:", error);
    }
  };

  const toggleIndividualSummary = (billId, type) => {
    setIndividualSummaryState(prev => {
      const next = new Map(prev);
      const current = next.get(billId) || { digest: false, ai: false };
      next.set(billId, { ...current, [type]: !current[type] });
      return next;
    });
  };

  const handleToggleReview = async (bill) => {
    try {
      const res = await API.toggleBillReview({
        user_id: auth.user.user_id,
        bill_id: bill.bill_id,
        bill_version_id: bill.bill_version_id,
      });
      setReviewedBills(prev => {
        const next = new Map(prev);
        if (res.data.reviewed) {
          next.set(bill.bill_version_id, res.data.reviewed_at);
        } else {
          next.delete(bill.bill_version_id);
        }
        return next;
      });
    } catch (err) {
      console.error("Error toggling bill review:", err);
    }
  };

  const handleBookmarkClick = async (bill) => {

    setCurrentBillForModal(bill);
    
    try {
      const teamsRes = await API.getUserTeamsForBillTracking(bill.bill_id, auth.user.user_id);
      const options = teamsRes.data;

      if (Array.isArray(options) && options.length > 0) {
        // Store original tracking status to compare changes later
        setTeamTrackingOptions(options.map(opt => ({
          ...opt,
          originalIsTracking: opt.isTracking,
          clients: (opt.clients || []).map(client => ({ ...client, originalIsTracking: client.isTracking })),
        })));
        setInitialPersonalTracked(trackedBills.has(bill.bill_id));
        setCurrentBillForModal(bill);
      } else {
        // No teams, proceed with personal tracking directly
        handleTogglePersonalTrack(bill);
      }
    } catch (error) {
      console.error("Error fetching tracking options:", error);
      // Fallback to personal tracking if fetching teams fails
      handleTogglePersonalTrack(bill);
    }
  };

  const handleModalTrackingChange = (type, id, checked) => {
    if (type === 'personal') {
      setTrackedBills(prev => {
        const newSet = new Set(prev);
        if (checked) {
          newSet.add(currentBillForModal.bill_id);
        } else {
          newSet.delete(currentBillForModal.bill_id);
        }
        return newSet;
      });
    } else if (type === 'team') {
      setTeamTrackingOptions(prev => prev.map(team =>
        team.team_id === id ? { ...team, isTracking: checked } : team
      ));
    } else if (type === 'client') {
      const { teamId, clientId } = id;
      setTeamTrackingOptions(prev => prev.map(team =>
        team.team_id === teamId
          ? {
              ...team,
              clients: team.clients.map(client =>
                client.client_id === clientId ? { ...client, isTracking: checked } : client
              ),
            }
          : team
      ));
    }
  };

  const handleSaveTrackingSelections = async () => {
    if (!currentBillForModal) return;

    const bill = currentBillForModal;
    const promises = [];

    // Update personal tracking if changed
    const isNowPersonalTracked = trackedBills.has(bill.bill_id);
    if (isNowPersonalTracked !== initialPersonalTracked) {
        promises.push(API.trackBill({ user_id: auth.user.user_id, bill_id: bill.bill_id, bill_version_id: bill.bill_version_id, active_track: isNowPersonalTracked }));
    }

    // Update team tracking if changed and user has permission
    for (const teamOption of teamTrackingOptions) {
        if (teamOption.isTracking !== teamOption.originalIsTracking && teamOption.bill_manage_permission) {
            promises.push(API.updateTeamTrackedBill({
              team_id: teamOption.team_id,
              bill_id: bill.bill_id,
              active_track: teamOption.isTracking,
              user_id: auth.user.user_id
            }));
        }

        // Update Client/Stakeholder Group tracking if changed and user has permission
        if (teamOption.bill_manage_permission) {
            for (const client of (teamOption.clients || [])) {
                if (client.isTracking !== client.originalIsTracking) {
                    promises.push(API.updateClientTrackedBill({
                      team_id: teamOption.team_id,
                      client_id: client.client_id,
                      bill_id: bill.bill_id,
                      active_track: client.isTracking,
                      user_id: auth.user.user_id
                    }));
                }
            }
        }
    }

    await Promise.all(promises);

    // Sync teamTrackedBills Map to reflect any team tracking changes
    setTeamTrackedBills((prev) => {
      const next = new Map(prev);
      const activeTeamTags = teamTrackingOptions
        .filter((t) => t.isTracking)
        .map((t) => ({
          label: t.team_acronym || t.team_name.split(/\s+/).map(w => w[0]).join('').toUpperCase(),
          color: t.tag_color || null,
        }));
      if (activeTeamTags.length > 0) next.set(bill.bill_id, activeTeamTags);
      else next.delete(bill.bill_id);
      return next;
    });

    setCurrentBillForModal(null);
  };

  const lastBillElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const sortedBills = useMemo(() => sortBillsByMode(bills, filters.sort), [bills, filters.sort]);

  // Pre-compute which bills need a calendar date/location header, in sorted order
  const calendarHeaderMap = useMemo(() => computeCalendarHeaders(sortedBills, filters.sort), [sortedBills, filters.sort]);

  // Teams where the user can create reports (owner, account_manage, or bill_manage)
  const reportEligibleTeams = useMemo(() => userTeams.filter(m =>
    m.TeamAccount?.team_admin === auth.user?.user_id || m.account_manage === true || m.bill_manage === true
  ), [userTeams, auth.user]);

  const toggleReportCartBill = (bill) => {
    setReportCart(prev => {
      const next = new Map(prev);
      if (next.has(bill.bill_id)) {
        next.delete(bill.bill_id);
      } else {
        next.set(bill.bill_id, { bill, note: '' });
      }
      return next;
    });
  };

  const handleAddAllLoaded = () => {
    setReportCart(prev => {
      const next = new Map(prev);
      bills.forEach(b => { if (!next.has(b.bill_id)) next.set(b.bill_id, { bill: b, note: '' }); });
      return next;
    });
  };

  const handleReportNoteChange = (billId, note) => {
    setReportCart(prev => {
      const next = new Map(prev);
      const entry = next.get(billId);
      if (entry) next.set(billId, { ...entry, note });
      return next;
    });
  };

  const handleClearReportCart = () => {
    setReportCart(new Map());
    setReportNoteBillId(null);
  };

  const handleToggleReportMode = () => {
    setReportMode(v => !v);
    setReportNoteBillId(null);
  };

  // Keep the floating report cart bar aligned to the legislation container's actual
  // rendered width/position (rather than the full viewport), so it doesn't get covered
  // by the fixed sidebar. Re-measures on window resize and on the sidebar's collapse/expand
  // transition (which changes .main-content's width without firing a resize event).
  useEffect(() => {
    if (!reportMode) return undefined;
    const el = pageContainerRef.current;
    if (!el) return undefined;

    const updateRect = () => {
      const rect = el.getBoundingClientRect();
      // Below the tablet/mobile breakpoint the container is already nearly edge-to-edge
      // (no wide centering gap to protect against), so narrowing it further only
      // squeezes the cart bar's buttons — use the full container width there instead.
      const ratio = window.innerWidth <= 970 ? 1 : CART_BAR_WIDTH_RATIO;
      const width = rect.width * ratio;
      const left = rect.left + (rect.width - width) / 2;
      setCartBarRect({ left, width });
    };

    updateRect();

    const mainContent = document.querySelector('.main-content');
    const ro = new ResizeObserver(updateRect);
    if (mainContent) ro.observe(mainContent);
    window.addEventListener('resize', updateRect);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateRect);
    };
  }, [reportMode]);

  const selectedSessionNum = parseInt(filters.session_number, 10) || 0;
  const filteredLocations = locations.filter(loc => {
    if (!loc.description) return true;
    return /\s+x\s*\d+$/i.test(loc.description) ? selectedSessionNum > 0 : true;
  });

  return (
    <div className="row legislation-page-container" ref={pageContainerRef}>
      <PageTitle title="Legislation | 'elloGovna" />
      {/* <div className="row"> */}
        {/* Left Column: Filters */}
        <div className="legislation-filters col-lg-3 col-md-3 col-sm-12 mb-4 pe-lg-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h3 className="mb-0 fl-title">Filter Legislation</h3>
            <button 
              className="btn btn-sm btn-outline-secondary d-md-none"
              type="button"
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters {showFilters ? <i className="bi bi-chevron-up"></i> : <i className="bi bi-chevron-down"></i>}
            </button>
          </div>
          
          <div className={`fl-filters-inner mt-2 ${showFilters ? 'd-block' : 'd-none'} d-md-block`}>
            <form onSubmit={handleSearch}>

            <div className="mb-3 px-1">
              <input
                type="text"
                className="form-control form-control-sm"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by bill (e.g. SB 123) or subject"
              />
              <div className="form-text info-note mx-1">Use commas for multiple phrases (i.e. "public safety, taxes")</div>
            </div>

            <div className="row mb-2 px-2">
              <div className='col-3'>
                <label className='text-muted small bill-filter-title'>Author: </label>
              </div>
              <div className='col-9'>
                <FilterDropdown
                  name="author"
                  value={filters.author}
                  onChange={handleFilterChange}
                  options={[
                    { value: '', label: 'Any' },
                    ...authors.map(author => ({ value: String(author.legislators_tbl_id), label: author.legislator_name })),
                  ]}
                />
              </div>
              
            </div>

            <div className="row mb-2 px-2">
              <div className='col-3'>
                <label className='text-muted small bill-filter-title'>Location: </label>
              </div>
              <div className='col-9'>
                <FilterDropdown
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  options={[
                    { value: '', label: 'Any' },
                    ...filteredLocations.map(loc => ({ value: loc.location_code, label: loc.description?.replace(/\s+x\s*\d+$/i, '') })),
                  ]}
                />
              </div>
              
            </div>

            <div className="row mb-2 px-2">
              <div className='col-3'>
                <label className='text-muted small bill-filter-title'>Session: </label>
              </div>
              <div className='col-9'>
                <FilterDropdown
                  name="session"
                  value={filters.session}
                  onChange={handleFilterChange}
                  options={sessions.map(s => ({ value: s, label: s }))}
                />
              </div>
              
            </div>

            <div className="row mb-2 px-2">
              <div className='col-5'>
                <label className='text-muted small bill-filter-title'>Session Type: </label>
              </div>
              <div className='col-7'>
                <FilterDropdown
                  name="session_number"
                  value={filters.session_number}
                  onChange={handleFilterChange}
                  disabled={sessionTypes.length <= 1}
                  options={sessionTypes.map(st => ({ value: st, label: formatSessionType(st) }))}
                />
              </div>
              
            </div>

            <div className="row mb-2 px-2">
              <div className='col-3'>
                <label className='text-muted small bill-filter-title'>Status: </label>
              </div>
              <div className='col-9'>
                <FilterDropdown
                  name="status"
                  value={filters.status}
                  multi
                  onToggle={(statusValue) => handleMultiFilterToggle('status', statusValue)}
                  options={statuses.map(status => ({ value: status, label: status }))}
                />
              </div>
              
            </div>

            {auth.isAuthenticated && (
              <div className="row mb-2 mt-1 px-2">
                <div className='col-3'>
                  <label className='text-muted small bill-filter-title'>Tracked: </label>
                </div>
                <div className='col-9'>
                  <FilterDropdown
                    name="trackedBy"
                    value={filters.trackedBy}
                    onChange={handleFilterChange}
                    options={[
                      { value: '', label: 'Any' },
                      { value: 'myself', label: 'Myself' },
                      ...userTeams.map(membership => ({
                        value: String(membership.team_id),
                        label: membership.TeamAccount?.team_acronym
                          || membership.TeamAccount?.team_name.split(/\s+/).map(w => w[0]).join('').toUpperCase(),
                      })),
                    ]}
                  />
                </div>
              </div>
            )}

            {auth.isAuthenticated && filters.trackedBy && filters.trackedBy !== 'myself' && (() => {
              const selectedMembership = userTeams.find(m => String(m.team_id) === filters.trackedBy);
              const teamClients = selectedMembership?.TeamAccount?.TeamClients || [];
              if (teamClients.length === 0) return null;
              return (
                <div className="row mb-2 px-2">
                  <div className='col-3'>
                    <label className='text-muted small bill-filter-title'>Client: </label>
                  </div>
                  <div className='col-9'>
                    <FilterDropdown
                      name="trackedByClient"
                      value={filters.trackedByClient}
                      onChange={handleFilterChange}
                      options={[
                        { value: '', label: 'Any' },
                        ...teamClients.map(client => ({ value: String(client.client_id), label: client.client_name })),
                      ]}
                    />
                  </div>
                </div>
              );
            })()}

            {auth.isAuthenticated && userTeams.length > 0 && (() => {
              const selectedTagIds = filters.tag_ids ? filters.tag_ids.split(',') : [];
              const showTeamLabel = userTeams.length > 1;
              return (
                <div className="row mb-2 px-2">
                  <div className='col-3'>
                    <label className='text-muted small bill-filter-title'>Tags: </label>
                  </div>
                  <div className='col-9'>
                    <div className="legislation-tags-dropdown" ref={tagsDropdownRef}>
                      <button
                        type="button"
                        className="form-select legislation-filter-select py-1 legislation-tags-dropdown-toggle"
                        onClick={() => setShowTagsDropdown(v => !v)}
                      >
                        {selectedTagIds.length > 0 ? `${selectedTagIds.length} tag${selectedTagIds.length > 1 ? 's' : ''} selected` : 'Any'}
                      </button>
                      {showTagsDropdown && (
                        <div className="legislation-tags-dropdown-panel">
                          {availableTeamTags.length === 0 ? (
                            <p className="text-muted small mb-0 px-2 py-1">No tags yet.</p>
                          ) : (
                            availableTeamTags.map(tag => {
                              const idStr = String(tag.tag_id);
                              const checked = selectedTagIds.includes(idStr);
                              return (
                                <label key={tag.tag_id} className="legislation-tags-dropdown-item form-check mb-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => handleMultiFilterToggle('tag_ids', tag.tag_id)}
                                  />
                                  <span className="legislation-tags-dropdown-label form-check-label">
                                    {tag.name}
                                    {showTeamLabel && <span className="legislation-tags-dropdown-acronym"> ({tag.team_label})</span>}
                                  </span>
                                </label>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="my-1 mx-2">
              <label className="form-label small text-muted mb-1">Activity</label>
              <div className="d-flex gap-1 mb-1">
                <div className="flex-fill">
                  <div className="text-muted" style={{fontSize:'0.65rem'}}>From</div>
                  <input type="date" className="form-control form-control-sm legislation-filter-select" name="version_date_from" value={filters.version_date_from} onChange={handleFilterChange} />
                </div>
                <div className="flex-fill">
                  <div className="text-muted" style={{fontSize:'0.65rem'}}>To</div>
                  <input type="date" className="form-control form-control-sm legislation-filter-select" name="version_date_to" value={filters.version_date_to} onChange={handleFilterChange} />
                </div>
              </div>
              <div className="d-flex gap-3 justify-content-end">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" name="version_introduced" value="true" id="versionIntroduced" checked={filters.version_introduced === 'true'} onChange={handleCheckboxChange} />
                  <label className="form-check-label small text-muted" htmlFor="versionIntroduced">Introduced</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" name="version_amended" value="true" id="versionAmended" checked={filters.version_amended === 'true'} onChange={handleCheckboxChange} />
                  <label className="form-check-label small text-muted" htmlFor="versionAmended">Amended</label>
                </div>
              </div>
            </div>

            <div className="saved-filters-panel my-3">
              <div
                className="d-flex align-items-center justify-content-between mb-1 saved-filters-toggle"
                onClick={() => setShowMiscFilters(v => !v)}
              >
                <span className="text-muted small fw-semibold">Misc. Filters</span>
                <i className={`bi ${showMiscFilters ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted small`}></i>
              </div>
              {showMiscFilters && (
                <>
                  <div className="mt-1 mx-2 d-flex align-items-center justify-content-between">
                    <label className="form-label small text-muted mb-0">Vote Req.</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="vote_required" value="Majority" id="voteMajority" checked={filters.vote_required === 'Majority'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="voteMajority">Majority</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="vote_required" value="Two Thirds" id="voteTwoThirds" checked={filters.vote_required === 'Two Thirds'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="voteTwoThirds">Two Thirds</label>
                      </div>
                    </div>
                  </div>
                  <hr className="my-0 mx-1"/>

                  <div className="my-1 mx-2 d-flex align-items-center justify-content-between">
                    <label className="form-label small text-muted mb-0">Fiscal Committee</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="fiscal_committee" value="Yes" id="fiscalYes" checked={filters.fiscal_committee === 'Yes'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="fiscalYes">Yes</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="fiscal_committee" value="No" id="fiscalNo" checked={filters.fiscal_committee === 'No'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="fiscalNo">No</label>
                      </div>
                    </div>
                  </div>
                  <hr className="my-0 mx-1"/>

                  <div className="my-1 mx-2 d-flex align-items-center justify-content-between">
                    <label className="form-label small text-muted mb-0">Local Program</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="local_program" value="Yes" id="localYes" checked={filters.local_program === 'Yes'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="localYes">Yes</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="local_program" value="No" id="localNo" checked={filters.local_program === 'No'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="localNo">No</label>
                      </div>
                    </div>
                  </div>
                  <hr className="my-0 mx-1"/>

                  <div className="my-1 mx-2 d-flex align-items-center justify-content-between">
                    <label className="form-label small text-muted mb-0">Urgency</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="urgency" value="Yes" id="urgencyYes" checked={filters.urgency === 'Yes'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="urgencyYes">Yes</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="urgency" value="No" id="urgencyNo" checked={filters.urgency === 'No'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="urgencyNo">No</label>
                      </div>
                    </div>
                  </div>
                  <hr className="my-0 mx-1"/>

                  <div className="mt-1 mx-2 d-flex align-items-center justify-content-between">
                    <label className="form-label small text-muted mb-0">Tax Levy</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="tax_levy" value="Yes" id="taxYes" checked={filters.tax_levy === 'Yes'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="taxYes">Yes</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="tax_levy" value="No" id="taxNo" checked={filters.tax_levy === 'No'} onChange={handleCheckboxChange} />
                        <label className="form-check-label small text-muted" htmlFor="taxNo">No</label>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className='row mt-3'>
              <button type="submit" className="legislation-search-button">Search</button>
              <button type="button" className="legislation-reset-button" onClick={handleReset}>Reset</button>
            </div>
            {filtersAreDirty && (
              <p className="fl-dirty-hint">To see the full filtered list, be sure to click Search.</p>
            )}
            </form>

            {/* Built-in Global Filters */}
            <div className="saved-filters-panel mt-3">
              <div
                className="d-flex align-items-center justify-content-between mb-1 saved-filters-toggle"
                onClick={() => setShowQuickFilters(v => !v)}
              >
                <span className="text-muted small fw-semibold">Quick Filters</span>
                <i className={`bi ${showQuickFilters ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted small`}></i>
              </div>
              {showQuickFilters && (
                <ul className="saved-filter-list list-unstyled mb-0">
                  {BUILTIN_FILTERS.map(bf => (
                    <li key={bf.id} className="saved-filter-item">
                      <button
                        className="saved-filter-name"
                        onClick={() => handleLoadBuiltinFilter(bf)}
                        title={`Bills ${bf.key === 'version_introduced' ? 'introduced' : 'amended'} since the start of yesterday`}
                      >
                        {bf.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Saved Filter Presets */}
            {auth.isAuthenticated && (
              <div className="saved-filters-panel mt-3">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-muted small fw-semibold">Saved Filters</span>
                  <div className="d-flex align-items-center gap-2">
                    {!filtersMatchDefault && (
                      <button
                        className="saved-filter-save-btn"
                        onClick={() => { setShowSaveForm(v => !v); setSaveFilterName(''); setShowSavedFilters(true); }}
                        title="Save current filters"
                      >
                        <i className="bi bi-plus-circle"></i> Add
                      </button>
                    )}
                    <i
                      className={`bi ${showSavedFilters ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted small saved-filters-toggle`}
                      onClick={() => setShowSavedFilters(v => !v)}
                    ></i>
                  </div>
                </div>

                {showSavedFilters && (
                <>
                {showSaveForm && (
                  <div className="saved-filter-save-form mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm mb-1"
                      placeholder="Filter set name…"
                      value={saveFilterName}
                      onChange={e => setSaveFilterName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSaveFilterPreset()}
                      autoFocus
                    />
                    <div className="d-flex gap-1">
                      <button className="saved-filter-confirm-btn flex-grow-1" onClick={handleSaveFilterPreset}>Save</button>
                      <button className="saved-filter-cancel-btn" onClick={() => setShowSaveForm(false)}>Cancel</button>
                    </div>
                  </div>
                )}

                {savedFilterPresets.length === 0 && !showSaveForm && (
                  <p className="text-muted small mb-0" style={{fontSize: '0.75rem'}}>No saved filters yet.</p>
                )}

                <ul className="saved-filter-list list-unstyled mb-0">
                  {savedFilterPresets.map(preset => (
                    <li key={preset.filter_id} className="saved-filter-item">
                      {editingPresetId === preset.filter_id ? (
                        <div className="d-flex gap-1 align-items-center">
                          <input
                            type="text"
                            className="form-control form-control-sm flex-grow-1"
                            value={editingPresetName}
                            onChange={e => setEditingPresetName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleRenameFilterPreset(preset.filter_id)}
                            autoFocus
                          />
                          <button className="saved-filter-confirm-btn" onClick={() => handleRenameFilterPreset(preset.filter_id)}>OK</button>
                          <button className="saved-filter-cancel-btn" onClick={() => setEditingPresetId(null)}>✕</button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-between">
                          <button
                            className="saved-filter-name"
                            onClick={() => handleLoadFilterPreset(preset)}
                            title="Load this filter set"
                          >
                            {preset.name}
                          </button>
                          <div className="d-flex gap-1">
                            <button
                              className="saved-filter-icon-btn"
                              onClick={() => { setEditingPresetId(preset.filter_id); setEditingPresetName(preset.name); }}
                              title="Rename"
                            ><i className="bi bi-pencil"></i></button>
                            <button
                              className="saved-filter-icon-btn"
                              onClick={() => handleDeleteFilterPreset(preset.filter_id)}
                              title="Delete"
                            ><i className="bi bi-trash"></i></button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                </>
                )}
              </div>
            )}

            {/* Workspace (team-shared) Saved Filter Presets */}
            {auth.isAuthenticated && userTeams.length > 0 && (
              <div className="saved-filters-panel mt-3">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-muted small fw-semibold">Workspace Filters</span>
                  <div className="d-flex align-items-center gap-2">
                    {isSelectedFilterTeamOwner && !filtersMatchDefault && (
                      <button
                        className="saved-filter-save-btn"
                        onClick={() => { setShowTeamSaveForm(v => !v); setSaveTeamFilterName(''); setShowTeamSavedFilters(true); }}
                        title="Save current filters to the Workspace"
                      >
                        <i className="bi bi-plus-circle"></i> Add
                      </button>
                    )}
                    <i
                      className={`bi ${showTeamSavedFilters ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted small saved-filters-toggle`}
                      onClick={() => setShowTeamSavedFilters(v => !v)}
                    ></i>
                  </div>
                </div>

                {showTeamSavedFilters && (
                <>
                {userTeams.length > 1 && (
                  <select
                    className="form-select form-select-sm mb-2"
                    value={selectedFilterTeamId || ''}
                    onChange={e => setSelectedFilterTeamId(Number(e.target.value))}
                  >
                    {userTeams.map(membership => (
                      <option key={membership.team_id} value={membership.team_id}>
                        {membership.TeamAccount?.team_name}
                      </option>
                    ))}
                  </select>
                )}

                {showTeamSaveForm && (
                  <div className="saved-filter-save-form mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm mb-1"
                      placeholder="Filter set name…"
                      value={saveTeamFilterName}
                      onChange={e => setSaveTeamFilterName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSaveTeamFilterPreset()}
                      autoFocus
                    />
                    <div className="d-flex gap-1">
                      <button className="saved-filter-confirm-btn flex-grow-1" onClick={handleSaveTeamFilterPreset}>Save</button>
                      <button className="saved-filter-cancel-btn" onClick={() => setShowTeamSaveForm(false)}>Cancel</button>
                    </div>
                  </div>
                )}

                {teamSavedFilterList.length === 0 && !showTeamSaveForm && (
                  <p className="text-muted small mb-0" style={{fontSize: '0.75rem'}}>No Workspace filters yet.</p>
                )}

                <ul className="saved-filter-list list-unstyled mb-0">
                  {teamSavedFilterList.map(preset => (
                    <li key={preset.filter_id} className="saved-filter-item">
                      {editingTeamPresetId === preset.filter_id ? (
                        <div className="d-flex gap-1 align-items-center">
                          <input
                            type="text"
                            className="form-control form-control-sm flex-grow-1"
                            value={editingTeamPresetName}
                            onChange={e => setEditingTeamPresetName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleRenameTeamFilterPreset(preset.filter_id)}
                            autoFocus
                          />
                          <button className="saved-filter-confirm-btn" onClick={() => handleRenameTeamFilterPreset(preset.filter_id)}>OK</button>
                          <button className="saved-filter-cancel-btn" onClick={() => setEditingTeamPresetId(null)}>✕</button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-between">
                          <button
                            className="saved-filter-name"
                            onClick={() => handleLoadFilterPreset(preset)}
                            title="Load this filter set"
                          >
                            {preset.name}
                          </button>
                          {isSelectedFilterTeamOwner && (
                            <div className="d-flex gap-1">
                              <button
                                className="saved-filter-icon-btn"
                                onClick={() => { setEditingTeamPresetId(preset.filter_id); setEditingTeamPresetName(preset.name); }}
                                title="Rename"
                              ><i className="bi bi-pencil"></i></button>
                              <button
                                className="saved-filter-icon-btn"
                                onClick={() => handleDeleteTeamFilterPreset(preset.filter_id)}
                                title="Delete"
                              ><i className="bi bi-trash"></i></button>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Results */}
        <div className="legislation-list col-lg-9 col-md-8 ms-1">
          <div className="fl-list-controls">
            <FilterDropdown
              name="sort"
              value={filters.sort}
              onChange={handleFilterChange}
              autoWidth
              options={[
                { value: 'newest', label: 'Newest' },
                {
                  value: 'file_location',
                  disabled: !(filters.location === 'Assembly Floor' || filters.location === 'Senate Floor'),
                  label: `File # ${!(filters.location === 'Assembly Floor' || filters.location === 'Senate Floor') ? '(Select Floor Location)' : ''}`,
                },
                { value: 'hearing_date', label: 'Hearing Date' },
                { value: 'calendar', label: 'Calendar View' },
                { value: 'az', label: 'Bill # (Increasing)' },
                { value: 'za', label: 'Bill # (Decreasing)' },
              ]}
            />
            <div className="d-flex align-items-center gap-1 flex-wrap fl-summary-toggle-group">
              <button
                type="button"
                className={`legislation-summary-mode-btn${filters.showDigest ? ' active' : ''}`}
                onClick={() => setFilters(prev => ({ ...prev, showDigest: !prev.showDigest }))}
              >
                <span className="d-none d-sm-inline">{filters.showDigest ? 'Hide Digest Summaries' : 'Show Digest Summaries'}</span>
                <span className="d-inline d-sm-none">Digest</span>
              </button>
              {aiSummarySessions.includes(filters.session || getCurrentSession()) && (
                <button
                  type="button"
                  className={`legislation-summary-mode-btn${filters.showAi ? ' active' : ''}`}
                  onClick={() => setFilters(prev => ({ ...prev, showAi: !prev.showAi }))}
                >
                  <span className="d-none d-sm-inline">{filters.showAi ? 'Hide AI Summaries' : 'Show AI Summaries'}</span>
                  <span className="d-inline d-sm-none">AI Summary</span>
                </button>
              )}
              {reportEligibleTeams.length > 0 && (
                <button
                  type="button"
                  className={`legislation-summary-mode-btn${reportMode ? ' active' : ''}`}
                  onClick={handleToggleReportMode}
                >
                  <span className="d-none d-sm-inline">{reportMode ? 'Exit Build Report' : 'Build Report'}</span>
                  <span className="d-inline d-sm-none">{reportMode ? 'Exit Report' : 'Build Report'}</span>
                </button>
              )}
            </div>
          </div>
          <div className="legislation-table-container">
            {sortedBills.map((bill, index) => {
              const isLast = bills.length === index + 1;
              const { billDisplay, authorName, location, status, fileLocationDisplay, hearingInfo, isEnrolled } = deriveBillDisplayFields(bill);

              const calendarInfo = calendarHeaderMap.get(bill.bill_id);

              // Rendered in two places (top row on desktop/tablet-landscape, below the
              // bill title on tablet-portrait/mobile) so it doesn't crowd the top row
              // alongside the bookmark/external-link icons on narrow screens.
              const reportAndTags = (
                <>
                  {reportMode && (
                    <div className="d-flex align-items-center me-2">
                      <button
                        type="button"
                        className={`legislation-summary-mode-btn${reportCart.has(bill.bill_id) ? ' active' : ''}`}
                        onClick={() => toggleReportCartBill(bill)}
                      >
                        {reportCart.has(bill.bill_id) ? 'Added' : 'Add to Report'}
                      </button>
                      {reportCart.has(bill.bill_id) && (
                        <span
                          className="summary-toggle-link ms-2"
                          onClick={() => setReportNoteBillId(prev => prev === bill.bill_id ? null : bill.bill_id)}
                        >
                          {reportCart.get(bill.bill_id).note ? 'Edit Note' : 'Add Note'}
                        </span>
                      )}
                    </div>
                  )}
                  {auth.isAuthenticated && (trackedBills.has(bill.bill_id) || teamTrackedBills.has(bill.bill_id)) && (
                    <span className="bill-track-tags me-2">
                      {trackedBills.has(bill.bill_id) && (
                        <span className="hearing-track-tag hearing-track-personal">Personal</span>
                      )}
                      {(teamTrackedBills.get(bill.bill_id) || []).map(({ label, color }, ti) => {
                        const s = TAG_COLORS[color] || DEFAULT_TEAM_TAG;
                        return (
                          <span key={ti} className="hearing-track-tag" style={{ background: s.bg, color: s.text }}>{label}</span>
                        );
                      })}
                    </span>
                  )}
                </>
              );

              return (
                <React.Fragment key={bill.bill_id}>
                  {calendarInfo?.showDateHeader && (
                    <div className="calendar-date-header">{calendarInfo.headerDateStr}</div>
                  )}
                  {calendarInfo?.showLocHeader && (
                    <div className={`calendar-location-subheader${calendarInfo.headerLocStr.startsWith('A') ? ' calendar-location-subheader--assembly' : calendarInfo.headerLocStr.startsWith('S') ? ' calendar-location-subheader--senate' : ''}${calendarInfo.isFirstLocUnderDate ? '' : ' calendar-location-subheader--subsequent'}`}>
                      {calendarInfo.headerLocStr}
                    </div>
                  )}
                <div ref={isLast ? lastBillElementRef : null} className="card mb-2 bill-card">
                  <div className="card-body py-2 px-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div>
                        <Link to={`/legislation/bill/${bill.bill_id}`} className="bill-number text-decoration-none">
                          {billDisplay}
                        </Link>
                        <span className="mb-1 text-muted"> - {authorName}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <div className="bill-card-secondary bill-card-secondary--desktop">
                          {reportAndTags}
                        </div>
                        {auth.isAuthenticated && (
                          <div className="bill-tracking-dropdown-anchor me-2">
                            <i
                              className={`bill-tracking-bookmark bi ${
                                trackedBills.has(bill.bill_id) || teamTrackedBills.has(bill.bill_id)
                                  ? 'bi-bookmark-fill'
                                  : 'bi-bookmark'
                              }`}
                              title={
                                teamTrackedBills.has(bill.bill_id)
                                  ? `Tracking ${billDisplay} (${teamTrackedBills.get(bill.bill_id).map(t => t.label).join(', ')})`
                                  : trackedBills.has(bill.bill_id)
                                    ? `Tracking ${billDisplay}`
                                    : `Click to track ${billDisplay}`
                              }
                              onClick={() => handleBookmarkClick(bill)}
                            ></i>
                            {currentBillForModal?.bill_id === bill.bill_id && userTeams.length > 0 && (
                              <div className="bill-tracking-dropdown">
                                <p className="bill-tracking-dropdown-title">Track {billDisplay}</p>
                                <div className="form-check mb-1">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`trackSelf-${bill.bill_id}`}
                                    checked={trackedBills.has(bill.bill_id)}
                                    onChange={(e) => handleModalTrackingChange('personal', null, e.target.checked)}
                                  />
                                  <label className="form-check-label" htmlFor={`trackSelf-${bill.bill_id}`}>
                                    Myself
                                  </label>
                                </div>
                                {userTeams.length > 0 && (
                                  <div className='bill-tracking-dropdown-subtitle mt-2'>Workspaces</div>
                                )}

                                {teamTrackingOptions.map(team => (
                                  <div key={team.team_id}>
                                    <div className="form-check mb-1">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`trackTeam-${bill.bill_id}-${team.team_id}`}
                                        checked={team.isTracking}
                                        disabled={!team.bill_manage_permission}
                                        onChange={(e) => handleModalTrackingChange('team', team.team_id, e.target.checked)}
                                      />
                                      <label className="form-check-label" htmlFor={`trackTeam-${bill.bill_id}-${team.team_id}`}>
                                        {team.team_name}
                                        {!team.bill_manage_permission && <span className="text-muted ms-1 small">(No permission)</span>}
                                      </label>
                                    </div>
                                    {/* Only reveal Clients/Stakeholder Groups once their Workspace is
                                        checked — the data's already in hand from the single tracking-options
                                        fetch, this just keeps the popover from being cluttered upfront. */}
                                    {team.isTracking && team.clients?.length > 0 && (
                                      <div className="bill-tracking-dropdown-clients ms-4">
                                        <div className='bill-tracking-dropdown-subtitle bill-tracking-dropdown-subtitle--sm'>{team.clientsLabel}</div>
                                        {team.clients.map(client => (
                                          <div key={client.client_id} className="form-check mb-1">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              id={`trackClient-${bill.bill_id}-${team.team_id}-${client.client_id}`}
                                              checked={client.isTracking}
                                              disabled={!team.bill_manage_permission}
                                              onChange={(e) => handleModalTrackingChange('client', { teamId: team.team_id, clientId: client.client_id }, e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor={`trackClient-${bill.bill_id}-${team.team_id}-${client.client_id}`}>
                                              {client.client_name}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                <div className="bill-tracking-dropdown-actions">
                                  <button className="bill-tracking-dropdown-save" onClick={handleSaveTrackingSelections}>Save</button>
                                  <button className="bill-tracking-dropdown-cancel" onClick={() => setCurrentBillForModal(null)}>Cancel</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <Link to={`/legislation/bill/${bill.bill_id}`} className="bill-profile-link">
                            <i className="bi bi-box-arrow-up-right"></i>
                        </Link>
                      </div>
                    </div>
                    <div className="mb-2 bill-title">{bill.title}</div>
                    <div className="bill-card-secondary bill-card-secondary--mobile">
                      {reportAndTags}
                    </div>
                    {/* <hr className="my-2 me-5"/> */}
                    {bill.LatestVersion && (
                      <div className="small text-muted mb-1">
                        <span className='fw-bold'>Version:</span> {bill.LatestVersion.version_status}
                        {bill.LatestVersion.updated_at && ` (${new Date(bill.LatestVersion.updated_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })})`}
                        {bill.LatestVersion.amendment_classification === 'Gut-and-amend' && (
                          <span className="leg-gutamend-badge ms-2" title="This version substantially or entirely replaced the bill's subject matter">
                            Gut-and-Amend
                          </span>
                        )}
                      </div>
                    )}
                    <div className="small text-muted d-flex flex-column flex-sm-row justify-content-between">
                      <div>
                        <span className="fw-bold">{status}</span>{status === 'Chaptered' && bill.chapter_number && bill.chapter_year && ` - Chapter ${bill.chapter_number}, Statutes of ${bill.chapter_year}`}{status !== 'Chaptered' && location && `: ${location}`}{status !== 'Chaptered' && !isEnrolled && bill.DailyFile?.floor_manager && <em> -- Floor Manager: {bill.DailyFile.floor_manager}</em>}
                      </div>
                        {fileLocationDisplay && (
                          <span className="bill-file-number me-2">{fileLocationDisplay}</span>
                        )}
                      {hearingInfo && (
                          <div className="legislation-hearing-info mt-1 mt-sm-0">
                              <u>Next Hearing:</u> {hearingInfo}
                          </div>
                      )}
                    </div>
                    {/* <hr className="my-2 mx-3"/> */}
                    {(bill.summary || bill.ai_summary || auth.isAuthenticated) && (
                      <div className="mt-2">
                        {(() => {
                          const indiv = individualSummaryState.get(bill.bill_id) || {};
                          const showDigest = (filters.showDigest || indiv.digest) && bill.summary;
                          const showAi = (filters.showAi || indiv.ai) && bill.ai_summary;
                          const aiEnabled = aiSummarySessions.includes(filters.session || getCurrentSession());
                          return (
                            <>
                              {showDigest && (
                                <div className="bill-summary-content mb-1">
                                  <u>Digest:</u> {bill.summary}
                                </div>
                              )}
                              {showAi && (
                                <div className="bill-summary-content mb-1">
                                  <u>AI Summary:</u> {bill.ai_summary.replace(/^#+\s+[^\n]*\n*/g, '').trim()}
                                </div>
                              )}
                              <div className="bill-card-actions">
                                {bill.summary && !filters.showDigest && (
                                  <span className="summary-toggle-link" onClick={() => toggleIndividualSummary(bill.bill_id, 'digest')}>
                                    {indiv.digest ? 'Hide Digest' : 'Show Digest'}
                                  </span>
                                )}
                                {bill.ai_summary && aiEnabled && !filters.showAi && (
                                  <span className="summary-toggle-link" onClick={() => toggleIndividualSummary(bill.bill_id, 'ai')}>
                                    {indiv.ai ? 'Hide AI Summary' : 'Show AI Summary'}
                                  </span>
                                )}
                                {auth.isAuthenticated && (
                                  <span
                                    className={`bill-review-btn ${reviewedBills.has(bill.bill_version_id) ? ' bill-review-btn--done' : ''}`}
                                    onClick={() => handleToggleReview(bill)}
                                  >
                                    {reviewedBills.has(bill.bill_version_id)
                                      ? `Reviewed ${new Date(reviewedBills.get(bill.bill_version_id)).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`
                                      : 'Mark Reviewed'}
                                  </span>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    {reportMode && reportNoteBillId === bill.bill_id && (
                      <div className="mt-2">
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          placeholder="Note for this bill on the report (optional, visible to the client/stakeholder)…"
                          value={reportCart.get(bill.bill_id)?.note || ''}
                          onChange={e => handleReportNoteChange(bill.bill_id, e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                </div>
                </React.Fragment>
              );
            })}
          {!hasMore && bills.length > 0 && <p className="text-center text-muted mt-3">No more bills to load.</p>}
          </div>

          {loading && (
            <div className="d-flex position-absolute top-50 start-50 translate-middle">
              <div className="spinner-border loading-spinner" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          
          {!loading && bills.length === 0 && <p className="text-center mt-5 text-muted">No bills found matching your criteria.</p>}

          {currentBillForModal && (
            <div className="bill-tracking-dropdown-backdrop" onClick={() => setCurrentBillForModal(null)} />
          )}
        </div>
      {/* </div> */}

      {reportMode && (
        <div
          className="report-cart-bar"
          style={cartBarRect ? { left: cartBarRect.left, width: cartBarRect.width, right: 'auto' } : undefined}
        >
          <span className="report-cart-count">{reportCart.size} bill{reportCart.size !== 1 ? 's' : ''} in report</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleAddAllLoaded}>
            <span className="d-none d-sm-inline">Add All Currently Loaded</span>
            <span className="d-inline d-sm-none">Add All</span>
          </button>
          {reportCart.size > 0 && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleClearReportCart}>
              Clear Selections
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm report-cart-review-btn"
            disabled={reportCart.size === 0}
            onClick={() => setShowCreateReportModal(true)}
          >
            <span className="d-none d-sm-inline">Review &amp; Create Report</span>
            <span className="d-inline d-sm-none">Create Report</span>
          </button>
        </div>
      )}

      {showCreateReportModal && (
        <CreateReportModal
          cart={reportCart}
          sortMode={filters.sort}
          eligibleTeams={reportEligibleTeams}
          onRemoveBill={toggleReportCartBill}
          onNoteChange={handleReportNoteChange}
          onClose={() => setShowCreateReportModal(false)}
          onCreated={() => {
            handleClearReportCart();
            setReportMode(false);
          }}
        />
      )}
    </div>
  );
}